/**
 * PictureSelect.ts — the gallery.
 *
 * A scrollable list of picture cards. Each card shows a smooth preview, title, palette strip, a
 * progress bar (the gallery REMEMBERS how far you got) and a status badge (New / NN% / ✓ Painted).
 * A segmented filter (All / Continue / Done) + smart sort surface what to paint next, so finishing
 * a picture feels permanent and there's always a "just one more". Tapping a card opens it.
 */
import { Container, Graphics, Sprite, Assets, Rectangle, type Texture } from 'pixi.js';
import { Scene } from '../app/Scene';
import type { Viewport } from '../app/AppContext';
import theme, { hex } from '../render/theme';
import { card, makeText, popIn, vGradient } from '../ui/widgets';
import { money } from '../util/format';
import { Tweens } from '../util/tween';
import { easeOutCubic } from '../util/ease';
import { Juice } from '../fx/Juice';
import { getPictureById } from '../pictures/library';
import type { PictureSummary } from '../net/contract';
import { Audio } from '../util/audio';
import { Progress } from '../state/progress';
import { PaintingScene } from './Painting';

// FOMO / discovery ribbons, keyed to the REAL seed pictures.
const RIBBONS: Record<string, { text: string; color: number } | undefined> = {
  'sports-car': { text: 'BIG BONUS PICTURE', color: theme.color.purple },
  'casino-chips': { text: 'POPULAR', color: theme.color.coral },
  'neon-city': { text: 'NEW', color: theme.color.teal },
  'cocktail': { text: 'NEW', color: theme.color.teal },
  'yacht': { text: 'QUICK PAINT', color: theme.color.gold2 },
};

// Filter is either one of the lifecycle pseudo-tags OR a discovery tag name.
type Filter = 'all' | 'progress' | 'done' | { tag: string };

export class PictureSelect extends Scene {
  private scroller = new Container();
  private header = new Container();
  private bg = new Graphics();
  private cards: Container[] = [];
  private scrollY = 0;
  private scrollVel = 0;
  private contentH = 0;
  private dragging = false;
  private dragStartY = 0;
  private dragStartScroll = 0;
  private lastMoveY = 0;
  private filter: Filter = 'all';
  private tabs = new Container();
  private tabsClip = new Container();   // masked viewport that scrolls this.tabs horizontally
  private tabsMask = new Graphics();
  private tabsFade = new Graphics();    // right-edge gradient hinting "more categories →"
  private tabsX = 0;                    // current horizontal scroll (<= 0)
  private tabsMaxScroll = 0;            // how far the strip can scroll
  private tabsDragged = false;          // set during a drag so the chip tap is suppressed
  private balanceText!: ReturnType<typeof makeText>;
  private metaText!: ReturnType<typeof makeText>;
  private vp!: Viewport;

  enter(): void {
    this.addChild(this.bg, this.scroller, this.header);
    this.buildHeader();
    this.rebuildCards();
    this.setupScroll();
    this.ctx.store.events.on('balance', (b) => {
      if (this.balanceText) this.balanceText.text = money(b);
    });
    this.layout(this.ctx.viewport);
  }

  private buildHeader(): void {
    const title = makeText('Gallery', { size: 28, weight: '800', color: theme.color.ink });
    title.label = 'title';
    const sub = makeText('Pick a picture · paint to win', { size: 14, weight: '700', color: theme.color.muted });
    sub.label = 'sub';
    this.metaText = makeText('', { size: 13, weight: '800', color: theme.color.teal });
    this.metaText.label = 'meta';

    const chip = new Container();
    chip.label = 'balchip';
    const chipBg = new Graphics();
    const coin = new Graphics();
    this.balanceText = makeText(money(this.ctx.store.balance), { size: 16, weight: '800', color: theme.color.goldText });
    chip.addChild(chipBg, coin, this.balanceText);
    (chip as Container & { _bg?: Graphics; _coin?: Graphics })._bg = chipBg;
    (chip as Container & { _coin?: Graphics })._coin = coin;

    this.tabsClip.addChild(this.tabs);
    this.tabsClip.mask = this.tabsMask;
    this.header.addChild(title, sub, this.metaText, chip, this.tabsClip, this.tabsMask, this.tabsFade);
    this.setupTabsScroll();
    this.buildTabs();
  }

  /** Horizontal drag-scroll for the category strip (the chips overflow on a phone). */
  private setupTabsScroll(): void {
    let active = false;
    let startPx = 0;
    let startX = 0;
    this.tabsClip.eventMode = 'static';
    this.tabsClip.on('pointerdown', (e) => {
      active = true;
      this.tabsDragged = false;
      startPx = e.global.x;
      startX = this.tabsX;
    });
    this.tabsClip.on('globalpointermove', (e) => {
      if (!active) return;
      const dx = e.global.x - startPx;
      if (Math.abs(dx) > 5) this.tabsDragged = true;
      if (this.tabsDragged) {
        this.tabsX = Math.max(-this.tabsMaxScroll, Math.min(0, startX + dx));
        this.tabs.x = this.tabsX;
        this.tabsFade.visible = this.tabsX > -this.tabsMaxScroll && this.tabsMaxScroll > 0;
      }
    });
    const end = () => { active = false; };
    this.tabsClip.on('pointerup', end);
    this.tabsClip.on('pointerupoutside', end);
  }

  private buildTabs(): void {
    this.tabs.removeChildren();
    // collect unique tag names from the gallery
    const tagSet = new Set<string>();
    for (const p of this.ctx.store.gallery) for (const t of p.tags ?? []) tagSet.add(t);
    const defs: { key: Filter; label: string }[] = [
      { key: 'all', label: 'All' },
      { key: 'progress', label: 'Continue' },
      { key: 'done', label: 'Done' },
      // discovery chips after the lifecycle ones
      ...Array.from(tagSet).sort().map((t) => ({ key: { tag: t } as Filter, label: t })),
    ];
    let x = 0;
    let activeX = 0;
    let activeW = 0;
    for (const d of defs) {
      const seg = new Container();
      const active = this.filterEquals(d.key);
      const t = makeText(d.label, { size: 13, weight: '800', color: active ? theme.color.white : theme.color.muted });
      const w = t.width + 30;
      const bg = new Graphics();
      bg.roundRect(0, 0, w, 32, 16).fill(active ? { color: theme.color.teal, alpha: 1 } : { color: theme.color.white, alpha: 0.7 });
      t.position.set(15, 8);
      seg.addChild(bg, t);
      seg.position.set(x, 0);
      seg.eventMode = 'static';
      seg.cursor = 'pointer';
      seg.on('pointertap', () => {
        if (this.tabsDragged) return; // this was a scroll, not a tap
        if (this.filterEquals(d.key)) return;
        this.filter = d.key;
        Audio.tap();
        this.buildTabs();
        this.rebuildCards();
        this.layout(this.vp);
      });
      this.tabs.addChild(seg);
      if (active) { activeX = x; activeW = w; }
      x += w + 8;
    }
    this.tabsContentW = x - 8;
    this.relayoutTabsScroll(activeX, activeW);
  }

  private tabsContentW = 0;
  private tabsViewW = 0;

  /** Recompute the scroll bounds and ensure the active chip is visible. */
  private relayoutTabsScroll(activeX = -1, activeW = 0): void {
    if (this.tabsViewW <= 0) return;
    this.tabsMaxScroll = Math.max(0, this.tabsContentW - this.tabsViewW);
    if (activeX >= 0 && this.tabsMaxScroll > 0) {
      // bring the active chip fully into the masked window
      const left = activeX + this.tabsX;
      const right = activeX + activeW + this.tabsX;
      if (right > this.tabsViewW) this.tabsX -= right - this.tabsViewW + 12;
      else if (left < 0) this.tabsX -= left - 12;
    }
    this.tabsX = Math.max(-this.tabsMaxScroll, Math.min(0, this.tabsX));
    this.tabs.x = this.tabsX;
    // right-edge fade only when there's more to the right
    this.tabsFade.visible = this.tabsX > -this.tabsMaxScroll && this.tabsMaxScroll > 0;
  }

  private filterEquals(b: Filter): boolean {
    const a = this.filter;
    if (typeof a === 'string' && typeof b === 'string') return a === b;
    if (typeof a === 'object' && typeof b === 'object') return a.tag === b.tag;
    return false;
  }

  /** filtered + smart-sorted picture list (continue first, then fresh, then done). */
  private visiblePics(): PictureSummary[] {
    const pics = this.ctx.store.gallery.slice();
    const prog = (id: string) => Progress.get(id);
    const filtered = pics.filter((p) => {
      const pr = prog(p.pictureId);
      if (this.filter === 'progress') return pr != null && pr.progress01 > 0 && !pr.completed;
      if (this.filter === 'done') return pr?.completed === true;
      if (typeof this.filter === 'object') return (p.tags ?? []).includes(this.filter.tag);
      return true;
    });
    const rank = (p: PictureSummary) => {
      const pr = prog(p.pictureId);
      if (pr && pr.progress01 > 0 && !pr.completed) return 0; // in progress: surface first
      if (!pr || pr.progress01 === 0) return 1;               // fresh
      return 2;                                               // done: sink
    };
    filtered.sort((a, b) => {
      const ra = rank(a), rb = rank(b);
      if (ra !== rb) return ra - rb;
      return (prog(b.pictureId)?.lastOpened ?? 0) - (prog(a.pictureId)?.lastOpened ?? 0);
    });
    return filtered;
  }

  private rebuildCards(): void {
    for (const c of this.cards) c.destroy({ children: true });
    this.cards = [];
    this.scroller.removeChildren();
    const pics = this.visiblePics();
    if (pics.length === 0) {
      const empty = makeText(
        this.filter === 'done' ? 'No finished pictures yet — paint one!' : 'Nothing here yet',
        { size: 15, weight: '700', color: theme.color.muted, align: 'center' },
      );
      empty.anchor.set(0.5, 0);
      empty.label = 'empty';
      this.scroller.addChild(empty);
    }
    for (const p of pics) {
      const c = this.makeCard(p);
      this.cards.push(c);
      this.scroller.addChild(c);
    }
    const done = Progress.completedCount();
    if (this.metaText) this.metaText.text = done > 0 ? `${done} / ${this.ctx.store.gallery.length} painted` : '';
    this.cards.forEach((c, i) => popIn(c, 40 + i * 55, 0.85));

    // "Paint next" beat: if we just returned from a completion, soft-pulse the top card so the
    // session naturally rolls into another picture instead of ending in an inert menu.
    const justFinished = this.ctx.store.lastCompletedAt > 0
      && Date.now() - this.ctx.store.lastCompletedAt < 30_000;
    if (justFinished && this.cards.length) this.pulsePaintNext(this.cards[0]);
  }

  private pulsePaintNext(card: Container): void {
    const ribbon = new Container();
    const t = makeText('PAINT NEXT', { size: 11, weight: '800', color: theme.color.white, letterSpacing: 0.6 });
    const w = t.width + 22;
    const bg = new Graphics();
    bg.roundRect(0, 0, w, 24, 12).fill(hex(theme.palette.purpleDeep));
    t.position.set(11, 5);
    ribbon.addChild(bg, t);
    // sit just under the "POPULAR/NEW" ribbon row at the top-left of the card preview
    ribbon.position.set(12, 44);
    ribbon.pivot.set(0, 12);
    card.addChild(ribbon);
    // soft scale pulse on the ribbon itself (not the whole card — that would fight layout scale)
    Tweens.to({
      from: 0, to: 1, duration: 1400,
      onUpdate: (t01) => {
        if (ribbon.destroyed) return;
        ribbon.scale.set(1 + Math.sin(t01 * Math.PI * 3) * 0.08);
      },
      onComplete: () => {
        Tweens.delay(2200, () => {
          if (ribbon.destroyed) return;
          Tweens.to({
            from: 1, to: 0, duration: 400,
            onUpdate: (a) => { if (!ribbon.destroyed) ribbon.alpha = a; },
            onComplete: () => { if (!ribbon.destroyed) ribbon.destroy({ children: true }); },
          });
        });
      },
    });
  }

  private makeCard(p: PictureSummary): Container {
    const def = getPictureById(p.pictureId);
    const pr = Progress.get(p.pictureId);
    const W = 360;
    const H = 240;
    const root = new Container();
    const shell = card(W, H, { fill: theme.color.white, radius: theme.radius.card });
    root.addChild(shell);

    const previewH = 150;
    const preview = new Container();
    const clip = new Graphics();
    clip.roundRect(0, 0, W, previewH, theme.radius.card).fill({ color: 0xffffff });
    preview.mask = clip;
    preview.addChild(clip);
    const tintBg = new Graphics();
    tintBg.rect(0, 0, W, previewH).fill(vGradient(def?.cardTint ?? theme.gradient.galleryBg.slice(0, 2) as [string, string], previewH));
    preview.addChild(tintBg);
    if (def) {
      const mini = new Container();
      const s = Math.min((W - 30) / def.view, (previewH - 24) / def.view);
      mini.scale.set(s);
      mini.position.set((W - def.view * s) / 2, (previewH - def.view * s) / 2);
      const g = new Graphics();
      for (const cell of def.cells) {
        const ph = def.palette.find((x) => x.colorId === cell.colorId)?.hex ?? theme.palette.hint;
        g.poly(cell.poly).fill({ color: hex(ph), alpha: 0.92 });
      }
      mini.addChild(g);
      preview.addChild(mini);
      // Skeleton shimmer while the smooth image is loading — drives the eye, hides the placeholder
      // pop-in if Assets.load is slow. Drawn on top of the vector placeholder, fades out once the
      // real sprite is faded in (audit minor: "no skeleton/shimmer/empty state while gallery loads").
      const skel = new Graphics();
      preview.addChild(skel);
      const skelTween = Tweens.to({
        from: 0, to: 1, duration: 1800,
        onUpdate: (t) => {
          if (skel.destroyed) return;
          // travelling diagonal highlight band across the preview rect
          skel.clear();
          const bw = W * 0.22, skew = previewH * 0.4;
          const x = -bw + (t % 1) * (W + bw * 2);
          skel.poly([x, 0, x + bw, 0, x + bw - skew, previewH, x - skew, previewH])
            .fill({ color: theme.color.white, alpha: 0.14 });
        },
      });
      // Gallery thumbnail prefers the ORIGINAL source photo (sharp, vivid — the "promise of
      // the finished result") and falls back to the posterised base. The painting view still
      // uses the posterised base since that matches the palette the player paints with.
      const thumbUrl = p.sourceImage ?? def.image;
      if (thumbUrl) {
        void Assets.load(thumbUrl).then((tex: Texture) => {
          if (root.destroyed) return;
          const sp = new Sprite(tex);
          const cover = Math.max(W / tex.width, previewH / tex.height);
          sp.scale.set(cover);
          sp.position.set((W - tex.width * cover) / 2, (previewH - tex.height * cover) / 2);
          sp.alpha = 0;
          preview.addChild(sp);
          Tweens.to({
            from: 0, to: 1, duration: 320, ease: easeOutCubic,
            onUpdate: (v) => { if (!sp.destroyed) sp.alpha = v; },
            onComplete: () => { skelTween.kill(); if (!skel.destroyed) skel.destroy(); },
          });
        });
      } else {
        // no smooth image to load; kill the shimmer quickly so the chrome stays clean
        Tweens.delay(900, () => { skelTween.kill(); if (!skel.destroyed) skel.destroy(); });
      }
    }
    root.addChild(preview);

    // progress bar across the bottom of the preview (the gallery remembers your work)
    const frac = pr?.progress01 ?? 0;
    if (frac > 0 && !pr?.completed) {
      const track = new Graphics();
      track.roundRect(12, previewH - 12, W - 24, 6, 3).fill({ color: theme.color.white, alpha: 0.5 });
      track.roundRect(12, previewH - 12, (W - 24) * frac, 6, 3).fill(hex(theme.palette.teal));
      root.addChild(track);
    }

    // ribbon (suppressed once you've started, so progress reads cleanly)
    const ribbon = RIBBONS[p.pictureId];
    if (ribbon && !pr) {
      const rb = new Container();
      const rt = makeText(ribbon.text, { size: 11, weight: '800', color: theme.color.white, letterSpacing: 0.5 });
      const rw = rt.width + 22;
      const rbg = new Graphics();
      rbg.roundRect(0, 0, rw, 24, 12).fill(ribbon.color);
      rt.position.set(11, 5);
      rb.addChild(rbg, rt);
      rb.position.set(12, 12);
      root.addChild(rb);
    }

    // status badge: ✓ Painted / NN%
    if (pr?.completed || frac > 0) {
      const done = pr?.completed === true;
      const badge = new Container();
      const txt = makeText(done ? '✓ Painted' : Math.round(frac * 100) + '%', {
        size: 12, weight: '800', color: done ? theme.color.white : theme.color.ink,
      });
      const bw = txt.width + 22;
      const bgc = new Graphics();
      bgc.roundRect(0, 0, bw, 26, 13).fill(done ? { color: hex(theme.palette.teal), alpha: 0.96 } : { color: theme.color.white, alpha: 0.94 });
      txt.position.set(11, 6);
      badge.addChild(bgc, txt);
      badge.position.set(12, 12);
      root.addChild(badge);
    }

    // size badge
    const sizeBadge = new Container();
    const sbBg = new Graphics();
    const sbT = makeText(`${p.sizeName} · ${p.totalCells} cells`, { size: 11, weight: '800', color: theme.color.ink });
    const sbw = sbT.width + 20;
    sbBg.roundRect(0, 0, sbw, 24, 12).fill({ color: theme.color.white, alpha: 0.92 });
    sbT.position.set(10, 5);
    sizeBadge.addChild(sbBg, sbT);
    sizeBadge.position.set(W - sbw - 12, previewH - 36);
    root.addChild(sizeBadge);

    const title = makeText(p.title, { size: 19, weight: '800', color: theme.color.ink });
    title.position.set(16, previewH + 12);
    root.addChild(title);

    const sw = new Container();
    let sx = 0;
    const maxSwatch = Math.min(p.palette.length, 10);
    for (let i = 0; i < maxSwatch; i++) {
      const pe = p.palette[i];
      const d = new Graphics();
      d.circle(8, 8, 8).fill(hex(pe.hex));
      d.circle(8, 8, 8).stroke({ width: 1, color: theme.color.white, alpha: 0.8 });
      d.position.set(sx, 0);
      sw.addChild(d);
      sx += 19;
    }
    if (p.palette.length > maxSwatch) {
      const more = makeText('+' + (p.palette.length - maxSwatch), { size: 12, weight: '800', color: theme.color.muted });
      more.position.set(sx + 2, 2);
      sw.addChild(more);
    }
    sw.position.set(16, previewH + 44);
    root.addChild(sw);

    const cta = pr?.completed ? 'Paint again' : frac > 0 ? 'Continue' : `From ${money(p.recommendedStakes[0] ?? 1)} / color`;
    const rec = makeText(cta, { size: 12, weight: '700', color: frac > 0 || pr?.completed ? theme.color.teal : theme.color.muted });
    rec.anchor.set(1, 0);
    rec.position.set(W - 16, previewH + 50);
    root.addChild(rec);

    // press feedback + open. If selectPicture takes time (network), the card holds a pressed-down
    // state with a spinner instead of freezing silently (audit feel gap "freezes during round-trip").
    root.eventMode = 'static';
    root.cursor = 'pointer';
    root.on('pointerdown', () => Juice.squash(root, 0.04, theme.motion.fast));
    root.on('pointertap', () => {
      Audio.unlock();
      Audio.tap();
      this.showCardLoading(root);
      this.select(p);
    });
    (root as Container & { _logicalW?: number; _logicalH?: number })._logicalW = W;
    (root as Container & { _logicalH?: number })._logicalH = H;
    return root;
  }

  private selecting = false;
  private async select(p: PictureSummary): Promise<void> {
    if (this.selecting) return; // ignore double-taps while the instance loads
    this.selecting = true;
    try {
      const inst = await this.ctx.transport.selectPicture(p.pictureId);
      this.ctx.scenes.go((ctx) => new PaintingScene(ctx, p, inst));
    } catch {
      this.selecting = false; // let the player retry on failure
      this.hideCardLoading();
    }
  }

  private activeLoadingCard: Container | null = null;
  private loadingSpinner: Graphics | null = null;
  private loadingTick = 0;

  /** Hold a pressed-down state + a small rotating arc on the tapped card while we round-trip. */
  private showCardLoading(card: Container): void {
    this.hideCardLoading();
    this.activeLoadingCard = card;
    card.alpha = 0.86;
    const spin = new Graphics();
    const lw = (card as Container & { _logicalW?: number })._logicalW ?? 360;
    const lh = (card as Container & { _logicalH?: number })._logicalH ?? 240;
    spin.position.set(lw - 30, lh - 30);
    card.addChild(spin);
    this.loadingSpinner = spin;
    this.loadingTick = 0;
  }

  private hideCardLoading(): void {
    if (this.activeLoadingCard) this.activeLoadingCard.alpha = 1;
    this.loadingSpinner?.destroy();
    this.loadingSpinner = null;
    this.activeLoadingCard = null;
  }

  private setupScroll(): void {
    this.scroller.eventMode = 'static';
    this.scroller.on('pointerdown', (e) => {
      this.dragging = true;
      this.dragStartY = e.global.y;
      this.lastMoveY = e.global.y;
      this.dragStartScroll = this.scrollY;
      this.scrollVel = 0;
    });
    const up = () => (this.dragging = false);
    this.scroller.on('pointerup', up);
    this.scroller.on('pointerupoutside', up);
    this.scroller.on('globalpointermove', (e) => {
      if (!this.dragging) return;
      const dy = e.global.y - this.dragStartY;
      const maxScroll = Math.max(0, this.contentH - (this.vp.height - this.headerH()));
      this.scrollY = Math.max(-50, Math.min(maxScroll + 50, this.dragStartScroll - dy));
      this.scrollVel = this.lastMoveY - e.global.y; // px/frame-ish, drives flick inertia
      this.lastMoveY = e.global.y;
      this.applyScroll();
    });
    this.eventMode = 'static';
    this.on('wheel', (e: WheelEvent) => {
      const maxScroll = Math.max(0, this.contentH - (this.vp.height - this.headerH()));
      this.scrollY = Math.max(0, Math.min(maxScroll, this.scrollY + e.deltaY));
      this.applyScroll();
    });
  }

  private headerH(): number {
    return (this.vp?.insetTop ?? 0) + 140; // title + balance + filter tabs
  }

  private applyScroll(): void {
    this.scroller.position.y = this.headerH() - this.scrollY;
  }

  override update(dtMs: number): void {
    if (!this.vp) return;
    if (this.loadingSpinner && !this.loadingSpinner.destroyed) {
      this.loadingTick += dtMs * 0.012;
      this.loadingSpinner.clear();
      const r = 12;
      this.loadingSpinner.arc(0, 0, r, this.loadingTick, this.loadingTick + Math.PI * 1.2)
        .stroke({ width: 3, color: hex(theme.palette.purple), alpha: 0.9, cap: 'round' });
      this.loadingSpinner.circle(0, 0, r).stroke({ width: 3, color: hex(theme.palette.purple), alpha: 0.18 });
    }
    const maxScroll = Math.max(0, this.contentH - (this.vp.height - this.headerH()));
    if (!this.dragging) {
      // flick inertia
      if (Math.abs(this.scrollVel) > 0.1 && this.scrollY >= 0 && this.scrollY <= maxScroll) {
        this.scrollY += this.scrollVel;
        this.scrollVel *= Math.pow(0.92, dtMs / 16.67);
        if (Math.abs(this.scrollVel) < 0.1) this.scrollVel = 0;
        this.applyScroll();
      }
      // ease back into bounds (elastic overscroll settle)
      const clamped = Math.max(0, Math.min(maxScroll, this.scrollY));
      if (Math.abs(clamped - this.scrollY) > 0.5) {
        this.scrollVel = 0;
        this.scrollY += (clamped - this.scrollY) * Math.min(1, dtMs / 120);
        this.applyScroll();
      }
    }
  }

  layout(v: Viewport): void {
    this.vp = v;
    this.bg.clear();
    this.bg.rect(0, 0, v.width, v.height).fill(vGradient(theme.gradient.galleryBg, v.height));
    this.bg.circle(v.width * 0.85, v.height * 0.12, 90).fill({ color: hex(theme.palette.purple), alpha: 0.06 });
    this.bg.circle(v.width * 0.1, v.height * 0.4, 120).fill({ color: hex(theme.palette.teal), alpha: 0.05 });

    const title = this.header.getChildByLabel('title') as Container | null;
    const sub = this.header.getChildByLabel('sub') as Container | null;
    const meta = this.header.getChildByLabel('meta') as Container | null;
    const chip = this.header.getChildByLabel('balchip') as Container & { _bg?: Graphics; _coin?: Graphics } | null;
    title?.position.set(18, v.insetTop + 16);
    sub?.position.set(18, v.insetTop + 52);
    if (meta) meta.position.set(18 + (sub?.width ?? 0) + 12, v.insetTop + 53);
    if (chip) {
      const tw = this.balanceText.width;
      const pw = tw + 56;
      const px = v.width - pw - 16;
      const py = v.insetTop + 20;
      chip._bg?.clear();
      chip._bg?.roundRect(0, 0, pw, 40, 20).fill(hex(theme.palette.gold1));
      chip._bg?.roundRect(0, 0, pw, 40, 20).stroke({ width: 1.5, color: hex(theme.palette.gold2), alpha: 0.5 });
      chip._coin?.clear();
      chip._coin?.circle(0, 0, 13).fill(hex(theme.palette.gold2));
      chip._coin?.circle(0, 0, 10).fill(theme.color.white);
      chip._coin?.circle(0, 0, 10).stroke({ width: 1.5, color: hex(theme.palette.gold2), alpha: 0.5 }); // match the HUD coin ring
      chip._coin?.position.set(22, 20);
      this.balanceText.position.set(40, 12);
      chip.position.set(px, py);
    }

    // category strip — masked horizontal scroller
    const tabsY = v.insetTop + 92;
    const tabsViewW = v.width - 36;
    this.tabsViewW = tabsViewW;
    this.tabsClip.position.set(18, tabsY);
    // explicit hit area so a drag can START anywhere in the strip (incl. gaps between chips),
    // not only directly on a chip
    this.tabsClip.hitArea = new Rectangle(0, -4, tabsViewW, 40);
    this.tabsMask.clear();
    this.tabsMask.rect(18, tabsY - 4, tabsViewW, 40).fill({ color: 0xffffff });
    // right-edge gradient fade ("more →")
    this.tabsFade.clear();
    const fadeW = 28;
    const steps = 7;
    for (let i = 0; i < steps; i++) {
      const fx = 18 + tabsViewW - fadeW + (fadeW / steps) * i;
      this.tabsFade.rect(fx, tabsY - 4, fadeW / steps + 1, 40).fill({ color: theme.color.bgLight2, alpha: (i / steps) * 0.9 });
    }
    this.relayoutTabsScroll();

    const pad = 16;
    const cw = v.width - pad * 2;
    let y = 0;
    const gap = 18;
    for (const c of this.cards) {
      const lw = (c as Container & { _logicalW?: number })._logicalW ?? 360;
      const lh = (c as Container & { _logicalH?: number })._logicalH ?? 240;
      const s = cw / lw;
      c.scale.set(s);
      c.position.set(pad, y);
      y += lh * s + gap;
    }
    const emptyMsg = this.scroller.getChildByLabel('empty') as Container | null;
    if (emptyMsg) emptyMsg.position.set(v.width / 2, 40);
    this.contentH = y;
    this.applyScroll();
  }
}
